import { useMemo } from "react";
import { t } from "ttag";

import { DatabaseEditConnectionForm } from "metabase/admin/databases/components/DatabaseEditConnectionForm";
import { useGetDatabaseQuery, useUpdateDatabaseMutation } from "metabase/api";
import { ExternalLink } from "metabase/common/components/ExternalLink";
import { LoadingAndErrorWrapper } from "metabase/common/components/LoadingAndErrorWrapper";
import { useDocsUrl } from "metabase/common/hooks";
import { usePageTitle } from "metabase/hooks/use-page-title";
import { useDispatch } from "metabase/redux";
import { addUndo } from "metabase/redux/undo";
import { useNavigate, useParams } from "metabase/router";
import { Flex, Icon, Modal, Text } from "metabase/ui";
import * as Urls from "metabase/urls";
import { useCreateDestinationDatabaseMutation } from "metabase-enterprise/api";
import type { Database, DatabaseData } from "metabase-types/api";

import { paramIdToGetQuery } from "../utils";

import S from "./DestinationDatabaseConnectionModal.module.css";
import { pickPrefillFieldsFromPrimaryDb } from "./utils";

export const DestinationDatabaseConnectionModal = () => {
  const { databaseId = "", destinationDatabaseId } = useParams<{
    databaseId: string;
    destinationDatabaseId: string;
  }>();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // eslint-disable-next-line metabase/no-unconditional-metabase-links-render -- Admin settings
  const { url: docsUrl } = useDocsUrl("permissions/database-routing");

  const primaryDbReq = useGetDatabaseQuery(paramIdToGetQuery(databaseId));
  const destinationDbReq = useGetDatabaseQuery(
    paramIdToGetQuery(destinationDatabaseId),
  );
  const [createDistinationDatabase] = useCreateDestinationDatabaseMutation();
  const [updateDatabase] = useUpdateDatabaseMutation();

  const isLoading = primaryDbReq.isLoading || destinationDbReq.isLoading;
  const error = primaryDbReq.error || destinationDbReq.error;
  const isNewDatabase = destinationDatabaseId === undefined;

  const destinationDatabase = useMemo<Partial<Database> | undefined>(() => {
    const primaryDb = primaryDbReq.currentData;

    if (isNewDatabase) {
      return primaryDb ? pickPrefillFieldsFromPrimaryDb(primaryDb) : undefined;
    }

    return destinationDbReq.currentData;
  }, [isNewDatabase, primaryDbReq.currentData, destinationDbReq.currentData]);

  const addingNewDatabase = destinationDatabaseId === undefined;

  const handleCloseModal = (method = "push") => {
    const dbId = parseInt(databaseId, 10);
    if (method === "push") {
      navigate(Urls.viewDatabase(dbId));
    } else {
      navigate(Urls.viewDatabase(dbId), { replace: true });
    }
  };

  const handleCreateDestinationDatabase = async (database: DatabaseData) => {
    return createDistinationDatabase({
      router_database_id: parseInt(databaseId, 10),
      destination_database: database,
    }).unwrap();
  };

  const handleSaveDatabase = async (database: DatabaseData) => {
    if (typeof database.id === "number") {
      return updateDatabase({
        ...database,
        id: database.id,
        auto_run_queries: database.auto_run_queries ?? true,
      }).unwrap();
    } else {
      return handleCreateDestinationDatabase(database);
    }
  };

  const handleOnSubmit = () => {
    dispatch(
      addUndo({
        message: addingNewDatabase
          ? t`Destination database created successfully`
          : t`Destination database updated successfully`,
      }),
    );
    handleCloseModal("replace");
  };

  usePageTitle(destinationDatabase?.name || "");

  return (
    <Modal
      title={
        addingNewDatabase
          ? t`Add destination database`
          : t`Edit destination database`
      }
      opened
      onClose={handleCloseModal}
      padding="xxl"
      classNames={{
        content: S.modalRoot,
        header: S.modalHeader,
        body: S.modalBody,
      }}
    >
      <LoadingAndErrorWrapper loading={isLoading} error={error} noWrapper>
        <>
          <Flex
            py="sm"
            px="lg"
            mx="xxl"
            my="lg"
            bg="background_page-secondary"
            align="center"
            justify="space-between"
            bd="1px solid border-neutral"
            style={{ borderRadius: ".5rem" }}
          >
            <Text>{t`You can also add databases programmatically via the API.`}</Text>
            <ExternalLink
              key="link"
              href={docsUrl}
              style={{ display: "flex", alignItems: "center", gap: 4 }}
            >
              {t`Learn more`} <Icon name="share" aria-hidden />
            </ExternalLink>
          </Flex>

          <DatabaseEditConnectionForm
            database={destinationDatabase}
            isAttachedDWH={destinationDatabase?.is_attached_dwh ?? false}
            handleSaveDb={handleSaveDatabase}
            onSubmitted={handleOnSubmit}
            onCancel={handleCloseModal}
            config={{
              name: { isSlug: true },
              engine: { fieldState: "hidden" },
            }}
            autofocusFieldName="name"
            formLocation="admin"
          />
        </>
      </LoadingAndErrorWrapper>
    </Modal>
  );
};
